import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PlanCardProps {
  plan: {
    key: string;
    name: string;
    price: string;
    betaNote?: string;
    description: string;
    features: string[];
    available: boolean;
  };
  isCurrent: boolean;
  shopId?: string;
  billingEnabled?: boolean;
  isBetaTester?: boolean;
}

export const PlanCard = ({ plan, isCurrent, shopId, billingEnabled = true, isBetaTester = false }: PlanCardProps) => {
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!shopId) {
      toast.error('Shop ID not found');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: {
          shop_id: shopId,
          success_url: `${window.location.origin}/shop-profile?tab=billing&success=true`,
          cancel_url: `${window.location.origin}/shop-profile?tab=billing`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const isDisabledByBilling = plan.available && !billingEnabled;

  // Determine button text
  const getButtonText = () => {
    if (loading) return 'Loading...';
    if (isCurrent) return 'Current Plan';
    if (isDisabledByBilling) return 'Pending Go-Live Approval';
    if (!plan.available) return 'Coming Soon';
    if (isBetaTester && billingEnabled) return 'Set Up Billing';
    return 'Subscribe';
  };

  return (
    <Card className={`p-6 relative ${isCurrent ? 'border-primary' : ''}`} data-testid={`plan-card-${plan.key}`}>
      {isCurrent && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Current Plan</Badge>
      )}
      {!plan.available && (
        <Badge variant="secondary" className="absolute -top-3 left-1/2 -translate-x-1/2">
          Coming Soon
        </Badge>
      )}

      <div className="mb-4">
        <h3 className="text-xl font-semibold">{plan.name}</h3>
        <p className="text-3xl font-bold mt-2">
          {plan.price}
          {plan.price.startsWith('$') && <span className="text-base font-normal text-muted-foreground">/mo</span>}
        </p>
        {plan.betaNote && (
          <p className="text-sm text-muted-foreground mt-2">{plan.betaNote}</p>
        )}
        <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
      </div>

      <ul className="space-y-2 mb-6">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>

      {isDisabledByBilling && (
        <p className="text-xs text-muted-foreground mb-3">
          Billing will be enabled by OptiBay after Go-Live approval.
        </p>
      )}

      <Button
        className="w-full"
        onClick={handleSubscribe}
        disabled={!plan.available || isCurrent || loading || isDisabledByBilling}
        variant={isCurrent ? 'outline' : 'default'}
        data-testid={`plan-card-${plan.key}-subscribe`}
      >
        {getButtonText()}
      </Button>
    </Card>
  );
};
